var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FeatureFlag } from "healthex-common/features.dtos";
import { isErr } from "healthex-common/result";
import { action, computed, observable, runInAction } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { HXMenuController, InjectableMoreMenuController } from "./HXMenu";
import { NamedRoutes } from "./NamedRoutes";
import { popNestedDialogLevel, pushNestedDialogLevel } from "./NestedDialogHistory";
import { formatDisplayDate, refreshOrFindRecordsLabel, refreshRecordsRecommendedSubtitle } from "./PatientHistory.types";
import { PatientSourceDetailsViewModel } from "./PatientSourceDetailsViewModel";
import { SourceStatus } from "./PatientSources.types";
import { printScreen } from "./printScreen";
const t = getObservableT("patients");
function mapSourceDto(dto, status) {
    var _a, _b;
    return {
        id: dto.id,
        organizationName: dto.organizationName,
        organizationLogoUrl: (_a = dto.organizationLogoUrl) !== null && _a !== void 0 ? _a : null,
        address: (_b = dto.address) !== null && _b !== void 0 ? _b : null,
        lastRefreshedAtDisplay: dto.lastRefreshedAt ? formatDisplayDate(dto.lastRefreshedAt) : null,
        physicianCount: dto.physicianCount,
        visitCount: dto.visitCount,
        diagnosticCount: dto.diagnosticCount,
        status,
        moreMenuController: new HXMenuController(),
        brandId: dto.brandId,
        portalId: dto.portalId,
        endpointId: dto.endpointId,
    };
}
export class PatientSourcesViewModel {
    constructor(container) {
        this.fetchSequence = 0;
        this.disposer = new Disposer();
        this.wasProcessing = false;
        this.hasTrackedSourcesViewed = false;
        this.stopped = false;
        this.moreMenuHolder = new InjectableMoreMenuController();
        this.isDownloadingPdf = false;
        this.isPrinting = false;
        this.isLoading = true;
        this.connectedSources = [];
        this.hiddenSources = [];
        this.skippedSources = [];
        this.sourceDetailsViewModel = null;
        this.unhidingSourceId = null;
        this.container = container;
        this.routingUIManager = container.routingUIManager;
        this.dashboardUIManager = container.patientResearchDashboardUIManager;
        this.healthWalletService = container.healthWalletService;
        this.authManager = container.authManager;
        this.featureManager = container.featureManager;
        this.analyticsService = container.analyticsService;
    }
    run() {
        this.stopped = false;
        void this.fetchSources();
        this.disposer.reaction(() => this.dashboardUIManager.isUnifiedRecordProcessing, (isProcessing) => {
            if (this.wasProcessing && isProcessing !== true) {
                void this.fetchSources();
            }
            this.wasProcessing = isProcessing === true;
        }, { name: "refetchSourcesOnProcessingComplete", fireImmediately: true });
    }
    stop() {
        this.stopped = true;
        this.disposer.dispose();
        this.closeSourceDetailsDialog({ syncHistory: false });
    }
    async fetchSources() {
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        const seq = ++this.fetchSequence;
        this.isLoading = true;
        try {
            const response = await this.healthWalletService.getSources(patientId);
            if (seq !== this.fetchSequence || this.stopped)
                return;
            runInAction(() => {
                var _a;
                this.connectedSources = response.connected.map((dto) => mapSourceDto(dto, SourceStatus.CONNECTED));
                this.hiddenSources = response.hidden.map((dto) => mapSourceDto(dto, SourceStatus.HIDDEN));
                this.skippedSources = [...response.skipped, ...((_a = response.needsAuth) !== null && _a !== void 0 ? _a : [])].map((dto) => mapSourceDto(dto, SourceStatus.SKIPPED));
                this.isLoading = false;
            });
            if (!this.hasTrackedSourcesViewed) {
                this.hasTrackedSourcesViewed = true;
                if (!navigator.webdriver) {
                    void this.analyticsService.trackHeapEvent({
                        event: "SourcesViewed",
                        identity: patientId,
                        properties: { ...getHeapGlobalEventProperties() },
                    });
                }
            }
        }
        catch (error) {
            if (seq !== this.fetchSequence || this.stopped)
                return;
            console.error("[PatientSources] Failed to fetch sources", error);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
    get showHiddenSources() {
        return this.dashboardUIManager.showHiddenSources;
    }
    get visibleSources() {
        if (!this.showHiddenSources)
            return this.connectedSources;
        return [...this.connectedSources, ...this.hiddenSources, ...this.skippedSources];
    }
    get hasSources() {
        return this.connectedSources.length > 0 || this.hiddenSources.length > 0 || this.skippedSources.length > 0;
    }
    get hasVisibleSources() {
        return this.visibleSources.length > 0;
    }
    get hasHiddenOrSkippedSources() {
        return this.hiddenSources.length > 0 || this.skippedSources.length > 0;
    }
    get statTotals() {
        const { physicians, visits } = this.connectedSources.reduce((totals, source) => ({
            physicians: totals.physicians + source.physicianCount,
            visits: totals.visits + source.visitCount,
        }), { physicians: 0, visits: 0 });
        return { sources: this.connectedSources.length, physicians, visits };
    }
    openSourceDetails(source) {
        var _a;
        if (this.sourceDetailsViewModel)
            return;
        pushNestedDialogLevel();
        this.sourceDetailsViewModel = new PatientSourceDetailsViewModel(this.container, source.id, {
            onBeforeNavigate: () => this.closeSourceDetailsDialog({ syncHistory: false }),
            onHidden: () => {
                this.closeSourceDetailsDialog();
                if (this.dashboardUIManager.isUnifiedRecordProcessing !== true)
                    void this.fetchSources();
            },
        });
        this.sourceDetailsViewModel.run();
        if (!navigator.webdriver) {
            void this.analyticsService.trackHeapEvent({
                event: "SourceDetailsOpened",
                identity: (_a = this.authManager.id) !== null && _a !== void 0 ? _a : undefined,
                properties: { ...getHeapGlobalEventProperties(), sourceId: source.id },
            });
        }
    }
    get sourceDetailsDialogOpen() {
        return this.sourceDetailsViewModel !== null;
    }
    closeSourceDetailsDialog(opts) {
        if (!this.sourceDetailsViewModel)
            return;
        if ((opts === null || opts === void 0 ? void 0 : opts.syncHistory) !== false) {
            const nestedLevelOpen = this.sourceDetailsViewModel.statDialogOpen || this.sourceDetailsViewModel.recordDetailsDialogOpen;
            popNestedDialogLevel(nestedLevelOpen ? 2 : 1);
        }
        this.sourceDetailsViewModel.stop();
        this.sourceDetailsViewModel = null;
    }
    async onUnhideSource(source) {
        if (this.unhidingSourceId !== null)
            return;
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        this.unhidingSourceId = source.id;
        try {
            const result = await this.healthWalletService.unhideSource(patientId, source.id);
            if (isErr(result)) {
                console.error("[PatientSources] Failed to unhide source", result.error);
                return;
            }
            try {
                await this.dashboardUIManager.loadMostRecentRetrieval();
            }
            catch (error) {
                console.error("[PatientSources] Failed to refresh most recent retrieval after unhiding source", error);
            }
            if (this.dashboardUIManager.isUnifiedRecordProcessing !== true)
                void this.fetchSources();
            source.moreMenuController.closeMenu();
            if (!navigator.webdriver) {
                void this.analyticsService.trackHeapEvent({
                    event: "SourceUnhidden",
                    identity: patientId,
                    properties: { ...getHeapGlobalEventProperties(), sourceId: source.id },
                });
            }
        }
        catch (error) {
            console.error("[PatientSources] Failed to unhide source", error);
        }
        finally {
            runInAction(() => {
                this.unhidingSourceId = null;
            });
        }
    }
    get isAddProviderEnabled() {
        return this.featureManager.isFeatureEnabled(FeatureFlag.RO_ADD_RECORD_LOCATIONS_DIRECTLY);
    }
    onAddProvider() {
        if (!this.isAddProviderEnabled)
            return;
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE, {}, { addRecordLocationsDirectly: "true" });
    }
    onAddSkippedSource(source) {
        if (!this.isAddProviderEnabled)
            return;
        const { brandId, portalId, endpointId } = source;
        if (brandId && portalId && endpointId) {
            this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE, {}, { addRecordLocationsDirectly: "true", brandId, portalId, endpointId });
        }
        else {
            this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE);
        }
    }
    get moreMenuController() {
        return this.moreMenuHolder.moreMenuController;
    }
    setMoreMenuController(controller) {
        this.moreMenuHolder.setMoreMenuController(controller);
    }
    get processingStateText() {
        return this.dashboardUIManager.processingStateText;
    }
    get isRefreshRecommended() {
        return this.isUnifiedRecordProcessing || this.hasSources;
    }
    get refreshRecordsLabel() {
        return refreshOrFindRecordsLabel(this.isRefreshRecommended);
    }
    get refreshRecordsSubtitle() {
        return refreshRecordsRecommendedSubtitle();
    }
    onRefreshRecords() {
        this.moreMenuController.closeMenu();
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_DATA_UPDATE);
    }
    onFindMyRecords() {
        this.moreMenuController.closeMenu();
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_IAS_CONSENT, { step: "welcome" });
    }
    get isDownloadPdfEnabled() {
        return this.dashboardUIManager.isUnifiedRecordDownloadable === true;
    }
    get isUnifiedRecordProcessing() {
        return this.dashboardUIManager.isUnifiedRecordProcessing === true;
    }
    async onDownloadPdf() {
        if (!this.isDownloadPdfEnabled || this.isDownloadingPdf)
            return;
        this.isDownloadingPdf = true;
        try {
            await this.dashboardUIManager.openUnifiedRecordDownloadInNewWindow("pdf", {
                onBeforeFetch: () => {
                    runInAction(() => {
                        this.moreMenuController.closeMenu();
                    });
                },
                onNoDownloadLink: () => {
                    window.alert(t("We couldn't open a download link for your records. Please try again in a moment."));
                },
            });
        }
        catch (e) {
            console.error("[PatientSources] Failed to download PDF", e);
        }
        finally {
            runInAction(() => {
                this.isDownloadingPdf = false;
            });
        }
    }
    async onPrintRecords() {
        if (this.isPrinting)
            return;
        this.isPrinting = true;
        try {
            this.moreMenuController.closeMenu();
            await printScreen({});
        }
        catch (error) {
            console.error("[PatientSources] Failed to print records", error);
        }
        finally {
            runInAction(() => {
                this.isPrinting = false;
            });
        }
    }
    get toggleShowHiddenSourcesLabel() {
        return this.showHiddenSources ? t("Hide Hidden Sources") : t("Show Hidden Sources");
    }
    onToggleShowHiddenSources() {
        this.moreMenuController.closeMenu();
        this.dashboardUIManager.toggleShowHiddenSources();
    }
}
__decorate([
    observable
], PatientSourcesViewModel.prototype, "isDownloadingPdf", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "isPrinting", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "isLoading", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "connectedSources", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "hiddenSources", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "skippedSources", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "sourceDetailsViewModel", void 0);
__decorate([
    observable
], PatientSourcesViewModel.prototype, "unhidingSourceId", void 0);
__decorate([
    action
], PatientSourcesViewModel.prototype, "fetchSources", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "showHiddenSources", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "visibleSources", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "hasSources", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "hasVisibleSources", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "hasHiddenOrSkippedSources", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "statTotals", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "openSourceDetails", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "sourceDetailsDialogOpen", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "closeSourceDetailsDialog", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onUnhideSource", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "isAddProviderEnabled", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onAddProvider", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onAddSkippedSource", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "setMoreMenuController", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "processingStateText", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "isRefreshRecommended", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "refreshRecordsLabel", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "refreshRecordsSubtitle", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onRefreshRecords", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onFindMyRecords", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "isDownloadPdfEnabled", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "isUnifiedRecordProcessing", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onDownloadPdf", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onPrintRecords", null);
__decorate([
    computed
], PatientSourcesViewModel.prototype, "toggleShowHiddenSourcesLabel", null);
__decorate([
    action
], PatientSourcesViewModel.prototype, "onToggleShowHiddenSources", null);
